import { useState } from 'react';
import { FEVER_C } from '@oncocoach/care';
import type { AppSession, MeView, PatientView, TrackActionId } from '../../lib/types';
import type { AuthConfig } from '../../lib/home';
import { ApiError, api } from '../../lib/api';
import { fmtClock, fmtInt, fmtTemp, greeting, localHour, pct } from '../../lib/format';
import { firstName } from '../../lib/session';
import { Icon } from '../../components/Mark';
import { Card, Progress } from '../../components/Bits';

/** How each track action looks and where it goes. The track decides which of these a patient sees. */
const ACTION: Record<TrackActionId, { href: string; icon: Parameters<typeof Icon>[0]['name']; tone: string }> = {
  'log-temperature': { href: '#/temp/log', icon: 'temperature', tone: 'red' },
  symptoms: { href: '#/symptoms', icon: 'symptoms', tone: 'gold' },
  meds: { href: '#/health', icon: 'pill', tone: 'purple' },
  'log-intake': { href: '#/log/intake', icon: 'drop', tone: 'blue' },
  'log-weight': { href: '#/log/weight', icon: 'trend', tone: 'blue' },
  'log-steps': { href: '#/log/steps', icon: 'steps', tone: 'green' },
  coach: { href: '#/coach', icon: 'chat', tone: 'green' },
  'call-clinic': { href: '#/profile', icon: 'phone', tone: 'blue' },
};

/**
 * The dashboard. The temperature comes first on any track where a fever is an emergency, because
 * it is the one number that decides whether today is an ordinary day. Nothing cheerful is ever
 * rendered above a fever.
 */
export function HomeTab({ session, view, reload, say }: { session: AppSession; me: MeView; config: AuthConfig | null; view: PatientView; reload: () => Promise<void>; say: (s: string) => void; onSignOut: () => void }) {
  const [busyDose, setBusyDose] = useState<string | null>(null);
  const hour = localHour(view.now, view.profile.tzOffsetMinutes);
  const p = view.progress;
  const taken = view.today.filter((d) => d.taken).length;
  const temp = view.latestTemperature;
  const feverNow = temp !== null && temp.celsius >= FEVER_C && view.track.feverIsEmergency;

  const actions = [...view.track.quickActions];
  if (view.goals.some((g) => g.kind === 'steps') && !actions.some((a) => a.id === 'log-steps')) {
    actions.splice(1, 0, { id: 'log-steps', label: 'Log activity' });
  }
  if (view.goals.some((g) => g.kind === 'intake') && !actions.some((a) => a.id === 'log-intake')) {
    actions.splice(2, 0, { id: 'log-intake', label: 'Log fluids' });
  }

  const scheduleOf = (medicationId: string) => ((view.medications.find((m) => m.id === medicationId)?.times.length ?? 1) > 1 ? 'Twice daily' : 'Once daily');

  const takeDose = async (medicationId: string, slot: number) => {
    const key = `${medicationId}:${slot}`;
    setBusyDose(key);
    try {
      await api.postEvent({ type: 'take-med', medicationId, slot }, session.token);
      await reload();
    } catch (e) {
      say(e instanceof ApiError ? e.message : 'Could not record that dose.');
    } finally {
      setBusyDose(null);
    }
  };

  return (
    <>
      {feverNow ? (
        <div className="callout callout-urgent" role="alert">
          <strong>Your last temperature was {fmtTemp(temp.celsius)}C.</strong>
          <p>A fever during treatment is an emergency. Ring {view.practiceName}'s oncology emergency line now, or go to your nearest emergency department, and tell them you are having cancer treatment.</p>
        </div>
      ) : null}

      <section className="banner">
        <div className="banner-main">
          <h1>
            {greeting(hour)}, {firstName(view.profile.name)}
          </h1>
          <p className="sub">{view.cycle ? `Cycle ${view.cycle.cycle.number}, day ${view.cycle.day}${view.cycle.nadir ? ' — the stretch when infection matters most' : ''}` : view.track.focus}</p>
        </div>
        {view.streak.current > 0 ? (
          <span className="streak streak-lg">
            <span aria-hidden="true">✓</span> {view.streak.current} days checked in
          </span>
        ) : null}
        <a className="insight-strip" href="#/coach">
          <span className="bubble bubble-white bubble-sm">
            <Icon name="chat" size={18} />
          </span>
          <span className="row-main">
            <strong>Your coach</strong>
            <span>{view.insight ? `“${view.insight}”` : 'Ask anything about today, and it will be answered from your own record.'}</span>
          </span>
          <span className="chev" aria-hidden="true">
            ›
          </span>
        </a>
      </section>

      <div className="grid-2">
        <div className="stack">
          <Card>
            <div className="card-head">
              <h2>This week</h2>
              <Icon name="star" size={18} />
            </div>
            {view.track.feverIsEmergency ? (
              <div className="goal">
                <div className="goal-row">
                  <span>Temperature logged</span>
                  <span className={p.temperature.onTrack ? 'good' : 'warn'}>{p.temperature.onTrack ? 'On track' : 'Behind'}</span>
                </div>
                <Progress value={p.temperature.done} max={p.temperature.target} />
                <span className="hint">
                  {p.temperature.done}/{p.temperature.target} days
                </span>
              </div>
            ) : null}
            <div className="goal">
              <div className="goal-row">
                <span>Told us how you feel</span>
                <span className={p.symptomChecks.done >= p.symptomChecks.target ? 'good' : ''}>
                  {p.symptomChecks.done}/{p.symptomChecks.target} days
                </span>
              </div>
              <Progress value={p.symptomChecks.done} max={p.symptomChecks.target} tone="green" />
              <span className="hint">It is how your team sees you between appointments.</span>
            </div>
            <div className="goal">
              <div className="goal-row">
                <span>Medications taken</span>
                <span className={p.adherence.percent >= 95 ? 'good' : 'warn'}>{pct(p.adherence.percent)}</span>
              </div>
              <Progress value={p.adherence.percent} />
              <span className="hint">{p.adherence.daysComplete}/7 days completed</span>
            </div>
          </Card>

          <Card>
            <div className="card-head">
              <h2>Today's medications</h2>
              <span className="hint">
                {taken}/{view.today.length} taken
              </span>
            </div>
            {view.today.length === 0 ? <p className="hint">No medications on your plan yet.</p> : null}
            {view.today.map((d) => {
              const key = `${d.medicationId}:${d.slot}`;
              return (
                <button key={key} type="button" className={`dose ${d.taken ? 'taken' : ''}`} disabled={d.taken || busyDose === key} onClick={() => void takeDose(d.medicationId, d.slot)} aria-pressed={d.taken}>
                  <span className={`tick ${d.taken ? 'on' : ''}`}>{d.taken ? <Icon name="check" size={14} /> : null}</span>
                  <span className="row-main">
                    <span className={`row-title ${d.taken ? 'struck' : ''}`}>
                      {d.name}
                      {d.oralTherapy ? <span className="badge badge-key">Treatment</span> : null}
                    </span>
                    <span className="row-sub">
                      {scheduleOf(d.medicationId)} · {fmtClock(d.time)}
                    </span>
                  </span>
                  {!d.taken ? <span className="badge">Mark taken</span> : null}
                </button>
              );
            })}
            {view.medications.some((m) => m.oralTherapy) ? <p className="hint">Never double up or skip ahead on your treatment tablets. If you miss one, ring your team.</p> : null}
          </Card>
        </div>

        <div className="stack">
          <div className="tile-row">
            {view.track.feverIsEmergency ? (
              <a className="tile" href="#/temp/log">
                <span className="bubble bubble-red bubble-sm tile-icon">
                  <Icon name="temperature" size={17} />
                </span>
                <strong className="tile-value">{temp ? `${fmtTemp(temp.celsius)}C` : '—'}</strong>
                <span className="tile-label">Temperature</span>
                <span className={feverNow ? 'warn small' : 'hint small'}>{temp ? (feverNow ? 'Ring your team now' : 'Tap to log again') : 'Not logged today'}</span>
              </a>
            ) : (
              <a className="tile" href="#/log/steps">
                <span className="bubble bubble-green bubble-sm tile-icon">
                  <Icon name="steps" size={17} />
                </span>
                <strong className="tile-value">{fmtInt(view.stepsToday)}</strong>
                <span className="tile-label">Activity today</span>
                <span className="hint small">{p.steps.todayGoal ? `Goal: ${fmtInt(p.steps.todayGoal)}` : 'Whatever you manage'}</span>
              </a>
            )}
            <div className="tile">
              <span className="bubble bubble-blue bubble-sm tile-icon">
                <Icon name="cycle" size={17} />
              </span>
              <strong className="tile-value">{view.cycle ? `Day ${view.cycle.day}` : '—'}</strong>
              <span className="tile-label">{view.cycle ? `Cycle ${view.cycle.cycle.number}` : 'No cycle recorded'}</span>
              <span className={view.cycle?.nadir ? 'warn small' : 'hint small'}>{view.cycle?.nadir ? 'Infection risk highest' : view.track.label}</span>
            </div>
          </div>

          <Card>
            <h2>Quick actions</h2>
            <div className="quick-grid">
              {actions.map((a) => {
                const look = ACTION[a.id];
                return (
                  <a key={a.id} href={look.href} className="quick-item">
                    <span className={`bubble bubble-${look.tone} bubble-sm`}>
                      <Icon name={look.icon} size={18} />
                    </span>
                    {a.label}
                  </a>
                );
              })}
              <button type="button" className="quick-item" onClick={() => say(`Ring ${view.practiceName} on the number on your appointment card. OncoCoach does not place calls.`)}>
                <span className="bubble bubble-blue bubble-sm">
                  <Icon name="phone" size={18} />
                </span>
                Call my team
              </button>
            </div>
            {view.track.feverIsEmergency ? <p className="hint">{FEVER_C.toFixed(1)}°C or more is an emergency, whatever time it is.</p> : null}
          </Card>

          {view.inboxUnread > 0 ? (
            <a className="card row-link" href="#/inbox" style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <span className="bubble bubble-gold bubble-sm">
                <Icon name="bell" size={17} />
              </span>
              <span className="row-main">
                <strong>{view.inboxUnread} new from your care team</strong>
                <span className="hint">Open your messages</span>
              </span>
              <span className="chev" aria-hidden="true">
                ›
              </span>
            </a>
          ) : null}
        </div>
      </div>
    </>
  );
}
